import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Building2, LogOut } from "lucide-react";

export function Avatar({ user, size = 32 }) {
  const initials = (user?.name || user?.email || "?")
    .split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const dim = { width: size, height: size };
  if (user?.picture) {
    return (
      <img
        data-testid="user-avatar"
        src={user.picture}
        alt={user.name}
        referrerPolicy="no-referrer"
        style={dim}
        className="rounded-full object-cover border border-[#E5E5E5]"
      />
    );
  }
  return (
    <div
      data-testid="user-avatar"
      style={dim}
      className="rounded-full bg-[#0A2E26] text-[#C49A45] flex items-center justify-center text-xs font-bold"
    >
      {initials}
    </div>
  );
}

export function Navbar({ variant = "landing" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 border-b border-[#E5E5E5] bg-white/80 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#0A2E26] flex items-center justify-center">
            <Building2 className="w-4 h-4 text-[#C49A45]" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight text-[#0A2E26]">
            EquiBrick
          </span>
        </Link>

        <nav className="flex items-center gap-3">
          <Link to="/properties" data-testid="nav-properties" className="text-sm font-medium text-[#5C5C5C] hover:text-[#0A2E26] transition-colors hidden sm:block">
            Portfolio
          </Link>
          <Link to="/story" data-testid="nav-story" className="text-sm font-medium text-[#5C5C5C] hover:text-[#0A2E26] transition-colors hidden sm:block">
            Track Record
          </Link>
          <Link to="/management" data-testid="nav-management" className="text-sm font-medium text-[#5C5C5C] hover:text-[#0A2E26] transition-colors hidden sm:block">
            Management
          </Link>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin">
                  <Button variant="ghost" className="text-[#0A2E26]">Admin</Button>
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button variant="ghost" className="text-[#0A2E26]">Dashboard</Button>
              </Link>
              <Avatar user={user} />
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="border-[#0A2E26]/20 text-[#0A2E26] gap-2"
              >
                <LogOut className="w-4 h-4" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-[#0A2E26]">Log in</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md">
                  Become a Member
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
